import { defineStore } from 'pinia'
import type { AuthUser } from '@/types/user'
import { logError } from '@/utils/general'

// State type for the auth store
interface AuthState {
  loggedIn: boolean
  token: string
  tokenExpires: string
  user: AuthUser | null
  userId: number | null
  redirectUrl: string
  ipAddress: string
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    loggedIn: false,
    token: '',
    tokenExpires: '',
    user: null,
    userId: null,
    redirectUrl: '/account',
    ipAddress: ''
  }),
  getters: {
    isAuthenticated: (state): boolean => state.loggedIn,
    authId: (state): number | null => state.user?.user?._id || null,
    authUser: (state): AuthUser | null => state.user,
    needsToUpdatePaymentMethod: (state): boolean => {
      if (!state.user) {
        return false
      }

      try {
        const hasPaymentMethod = state.user.user?.paymentContracts?.edges.some((payment) => payment.node.active)

        return !hasPaymentMethod
      } catch (e) {
        logError(e)
        return false
      }
    },
    userCountry: (state): string | undefined => {
      const country = state.user?.user?.country?.name?.toLowerCase()
      return country
    },
    additionalPaymentData: (state) => {
      return {
        givenName: state.user?.user?.firstName,
        surname: state.user?.user?.lastName,
        ipAddress: state.ipAddress,
        userCountry: state.user?.user?.country?.name?.toLowerCase(),
        email: state.user?.user?.email
      }
    },
    waitDays: (state) => {
      return state.user?.user?.country?.expectedDeliveryDateDailyOrderAfterDays
    },
    returnAfterDay: (state) => {
      return state.user?.user?.country?.canDoReturnRequestAfterDay
    }
  },
  actions: {
    setAuthToken(token: string, expires: string) {
      this.token = token
      this.tokenExpires = expires
      this.loggedIn = !!token
    },
    setAuthUser(user: AuthUser | null) {
      this.loggedIn = !!user
      this.user = user
      this.userId = user?.user?._id || null
    },
    setRedirectUrl(url: string) {
      this.redirectUrl = url
    },
    setIpAddress(ipAddress: string) {
      this.ipAddress = ipAddress
    },
    logout() {
      this.token = ''
      this.loggedIn = false
      this.tokenExpires = ''
      this.user = null
      this.userId = null
      // Datadog is optional; guard if not available
      try {
        ;(this as any).$datadog?.clearUser?.()
      } catch (e) {
        console.error(e)
      }
    },
    async updateUser(variables: {
      id: string
      firstName?: string
      lastName?: string
      email?: string
      phoneNumber?: string
      dateOfBirth?: string
      avatar?: string
      isUser?: boolean
    }) {
      const mutation = `
        mutation UpdateAuthUser(
          $id: ID!
          $firstName: String
          $lastName: String
          $email: String
          $phoneNumber: String
          $dateOfBirth: String
          $avatar: String
          $isUser: Boolean = false
        ) {
          updateAuthUser(
            input: {
              id: $id
              firstName: $firstName
              lastName: $lastName
              email: $email
              phoneNumber: $phoneNumber
              dateOfBirth: $dateOfBirth
              avatar: $avatar
            }
          ) {
            authUser {
              _id
              id
              fullName
              firstName
              lastName
              email
              createdAt
              updatedAt
              dateOfBirth
              avatar
              user @include(if: $isUser) {
                _id
                id
                firstName
                lastName
                email
                phoneNumber
                country {
                  _id
                  id
                  name
                  canCancelSubscriptionAfterDays
                  expectedDeliveryDateDailyOrderAfterDays
                  canDoReturnRequestAfterDay
                }
                members {
                  edges {
                    node {
                      status
                    }
                  }
                }
                addresses {
                  edges {
                    node {
                      _id
                      id
                      street
                      fullStreet
                      streetType
                      houseNumber
                      annex
                      building
                      city
                      postalCode
                      country {
                        _id
                        id
                        name
                        hostNamePrefix
                        languages {
                          edges {
                            node {
                              id
                              iso
                            }
                          }
                        }
                      }
                    }
                  }
                }
                billingAddress {
                  _id
                  id
                  street
                  fullStreet
                  streetType
                  houseNumber
                  annex
                  building
                  city
                  postalCode
                  country {
                    _id
                    id
                    name
                    hostNamePrefix
                  }
                }
              }
            }
            clientMutationId
          }
        }
      `

      const {
        public: { API_URL }
      } = useRuntimeConfig()

      const { token } = useApolloToken()

      const response = await $fetch<{
        data?: { updateAuthUser?: { authUser: AuthUser } }
        errors?: any[]
      }>(`${API_URL}/graphql`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${token.value}`
        },
        body: {
          operationName: 'UpdateAuthUser',
          variables: { isUser: true, ...variables },
          query: mutation
        }
      })

      if (!response.data || !response.data.updateAuthUser) {
        throw response
      }

      const updatedData = response.data.updateAuthUser
      this.setAuthUser(updatedData.authUser)
      return updatedData
    },
    async updatePassword(variables: { id: string; currentPassword: string; newPassword: string }) {
      const mutation = `
        mutation UpdatePassword(
          $id: ID!
          $currentPassword: String!
          $newPassword: String!
        ) {
          updatePasswordAuthUser(
            input: {
              id: $id
              currentPassword: $currentPassword
              newPassword: $newPassword
            }
          ) {
            clientMutationId
          }
        }
      `

      const {
        public: { API_URL }
      } = useRuntimeConfig()

      const { token } = useApolloToken()

      const response = await $fetch<{
        data?: { updatePasswordAuthUser?: { clientMutationId?: string } }
        errors?: any[]
      }>(`${API_URL}/graphql`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${token.value}`
        },
        body: {
          operationName: 'UpdatePassword',
          variables,
          query: mutation
        }
      })

      if (!response.data || !response.data.updatePasswordAuthUser) {
        throw response
      }

      return response.data.updatePasswordAuthUser
    }
    // HTTP actions removed - data fetching now handled by useAuth composables
  }
})
